import { useMemo, useState } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersApi, ServiceProvidersTypes, ServiceProvidersUtils } from '@frontend/api-service-providers';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ConfirmationModal, useModalControl, Text, useAlert, Button, Heading } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { ManageProvidersAvailabilityBanner } from '../../components/manage-providers-availability-banner';
import { ProviderAvailabilityModal } from '../../components/ServiceProviderAvailabilityModal';
import { ServiceProviderModal } from '../../components/ServiceProviderModal';
import { ServiceProviderTable } from '../../components/ServiceProviderTable';
import { ScheduleSettingsSection } from '../../components/settings';
import { useGetWeaveProviderStatus } from '../../hooks/use-get-weave-provider-status';
import * as styles from './styles';

const { sortByName } = ServiceProvidersUtils;

type ManageServiceProvidersProps = {
  locationId?: string;
};

export function ManageServiceProviders({ locationId }: ManageServiceProvidersProps) {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: 450 });
  const isLargeDevice = useMatchMedia({ minWidth: breakpoints.medium.max });
  const { hasAiScheduleAccess } = useAiScheduleAccess();

  const { selectedLocationIds, accessibleLocationData, isSingleTypeScope } = useAppScopeStore();
  const applicationLocationId = selectedLocationIds[0] ?? '';
  const isMultiLocation = !isSingleTypeScope;
  const selectedLocationId =
    selectedLocationIds.length === 1 ? selectedLocationIds[0] : locationId || applicationLocationId || '';

  const locationData = accessibleLocationData?.[selectedLocationId];
  const isChildLocation = isMultiLocation && !!locationData?.groupId;

  const {
    canAddWeaveProvider,
    isLoading: isLoadingCreateAppointmentCapability,
    hasCreateAppointmentCapability,
  } = useGetWeaveProviderStatus(selectedLocationId);

  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>();

  const {
    data: providersData,
    refetch: refetchProvidersData,
    isLoading: isLoadingProvidersData,
    isFetching: isFetchingProvidersData,
  } = useQuery({
    queryKey: queryKeys.providersList([selectedLocationId]),
    queryFn: () => ServiceProvidersApi.listServiceProviders(selectedLocationId),
    onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'listServiceProviders' }),
  });

  const { data: parentLocationData } = useQuery(
    ['parentLocationData', selectedLocationId, locationData?.groupId ?? ''],
    () => (isChildLocation ? ServiceProvidersApi.getParentLocationData(locationData?.groupId ?? '') : undefined)
  );

  const unifyLocationNames = `${parentLocationData?.officeName ?? ''} / ${locationData?.name ?? ''}`;

  const {
    data: unifyProviderList,
    refetch: refetchUnifyProviderList,
    isFetching: isFetchingUnifyProviderList,
  } = useScopedQuery({
    queryKey: 'unifyProviders',
    queryFn: () => (isChildLocation ? ServiceProvidersApi.listUnifyProviders() : undefined),
    retry: 1,
  });

  const providers = useMemo(() => {
    return (providersData || []).sort(sortByName);
  }, [providersData]);

  const selectedProvider = useMemo(() => {
    if (!selectedProviderId || !providersData) {
      return {} as Partial<ServiceProvidersTypes.ServiceProvider>;
    }
    return providersData.find(({ id }) => id === selectedProviderId);
  }, [selectedProviderId, providersData]);

  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl();

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const { modalProps: cancelModalProps, triggerProps: cancelTriggerProps } = useModalControl();

  const { modalProps: providerAvailabilityModalProps, triggerProps: providerAvailabilityTriggerProps } =
    useModalControl();

  const handleConfirmDelete = async () => {
    if (!selectedProviderId) {
      return;
    }
    try {
      await ServiceProvidersApi.deleteServiceProvider(selectedProviderId);
      alert.success(t('Successfully deleted provider'));
    } catch (error: any) {
      alert.error(t('Failed to delete provider'));
    } finally {
      refetchProvidersData();
      setSelectedProviderId(undefined);
    }
  };

  const handleAddNew = () => {
    setSelectedProviderId(undefined);
    editTriggerProps.onClick();
  };

  const handleEdit = (providerId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    setSelectedProviderId(providerId);
    editTriggerProps.onClick();
  };

  const handleEditSchedule = (providerId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    setSelectedProviderId(providerId);
    providerAvailabilityTriggerProps.onClick();
  };

  const handleDelete = (providerId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    setSelectedProviderId(providerId);
    deleteTriggerProps.onClick();
  };

  const handleCancel = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    cancelTriggerProps.onClick();
  };

  return (
    <ScheduleSettingsSection transparentInSmallMode>
      {hasAiScheduleAccess && <ManageProvidersAvailabilityBanner />}
      <div>
        {isChildLocation && (
          <Text as={'h3'} css={styles.unifyLocationNames}>
            {unifyLocationNames}
          </Text>
        )}
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(3),
          }}
        >
          {!isMobile && (
            <Heading css={{ visibility: isLoadingProvidersData ? 'hidden' : 'visible' }} level={2}>
              {t('Providers: {{length}}', { length: providers.length })}
            </Heading>
          )}
          <div css={styles.actionPanel(isLargeDevice, isMobile)}>
            {canAddWeaveProvider && (
              <Button size='large' {...editTriggerProps} onClick={handleAddNew} css={{ marginRight: theme.spacing(1) }}>
                {t('Add Provider')}
              </Button>
            )}
            {!isMobile ? (
              <Button
                variant='secondary'
                size='large'
                iconName='sync'
                hoverLabel={t('Refresh providers')}
                onClick={() => refetchProvidersData?.()}
              />
            ) : (
              <Button
                variant='secondary'
                size='large'
                iconName='update'
                hoverLabel={t('Refresh providers')}
                onClick={() => refetchProvidersData?.()}
              />
            )}
          </div>
        </div>
      </div>
      <ServiceProviderTable
        providers={providers}
        hasCreateAppointmentCapability={hasCreateAppointmentCapability}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        handleEditSchedule={handleEditSchedule}
        disableProviderActions={hasAiScheduleAccess}
        isLoading={
          isLoadingProvidersData ||
          isLoadingCreateAppointmentCapability ||
          isFetchingProvidersData ||
          isFetchingUnifyProviderList
        }
      />
      <ServiceProviderModal
        refetch={refetchProvidersData}
        provider={selectedProvider as ServiceProvidersTypes.ServiceProvider}
        {...editModalProps}
        unifyProviders={unifyProviderList}
        refetchUnifyProviders={refetchUnifyProviderList}
        isMultiLocation={isMultiLocation}
        isChildLocation={isChildLocation}
        locationId={selectedLocationId}
      />
      <ProviderAvailabilityModal
        {...providerAvailabilityModalProps}
        primaryActionName={t('Save')}
        secondaryActionName={t('Cancel')}
        selectedProvider={selectedProvider}
        handleCancel={handleCancel}
        locationId={selectedLocationId}
      />
      <ConfirmationModal
        {...deleteModalProps}
        title={t('Delete Provider')}
        destructive
        message={t('Are you sure you want to delete {{providerName}}?', {
          providerName: `${selectedProvider?.firstName} ${selectedProvider?.lastName}`,
        })}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onCancel={deleteModalProps.onClose}
        onConfirm={handleConfirmDelete}
      />
      <ConfirmationModal
        {...cancelModalProps}
        title={t('Unsaved Changes')}
        destructive
        message={t('Your changes will not be saved. Are you sure you want to leave this page?')}
        confirmLabel={t('Leave')}
        cancelLabel={t('Stay')}
        onCancel={cancelModalProps.onClose}
        onConfirm={providerAvailabilityModalProps.onClose}
      />
    </ScheduleSettingsSection>
  );
}
